import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useAsync } from '../../hooks/useAsync';
import { complianceScoreColor } from '../../utils/scoreColor';
import { ComplianceSummary } from '../../types/compliance';
import { timeEntriesAPI } from '../../services/api';

interface ComplianceCoachProps {
  threshold?: number;
  // Any changing value triggers a refetch (e.g. the pending-entries array).
  refreshKey?: unknown;
}

const ComplianceCoach: React.FC<ComplianceCoachProps> = ({
  threshold = 60,
  refreshKey,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [queuing, setQueuing] = useState(false);

  const { data: summary, loading } = useAsync<ComplianceSummary>(
    () =>
      timeEntriesAPI
        .getComplianceSummary({ threshold })
        .then((r) => r.data as ComplianceSummary),
    [threshold, refreshKey],
  );

  const handleAnalyzePending = useCallback(async () => {
    setQueuing(true);
    try {
      const res = await timeEntriesAPI.analyzePending();
      const count = res.data?.queued_count ?? 0;
      showToast(
        t('approval.complianceQueued', {
          count,
          defaultValue: `Queued ${count} entries for analysis. Refresh in a moment to see updated scores.`,
        }),
        'success',
      );
    } catch (error) {
      console.error('Failed to queue compliance analysis:', error);
      showToast(
        t('approval.complianceQueueFailed', {
          defaultValue: 'Could not queue analysis.',
        }),
        'error',
      );
    } finally {
      setQueuing(false);
    }
  }, [showToast, t]);

  // Nothing pending, or everything already compliant — stay out of the way.
  if (loading || !summary) return null;
  if (summary.low_score_count === 0 && summary.unscored_count === 0) return null;

  const card = isDark
    ? 'bg-amber-500/10 border-amber-500/30'
    : 'bg-amber-50 border-amber-200';
  const heading = isDark ? 'text-amber-300' : 'text-amber-800';
  const body = isDark ? 'text-amber-200/80' : 'text-amber-700';

  return (
    <div className={`mb-6 rounded-xl border ${card} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <svg
            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${heading}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div>
            <h3 className={`text-sm font-semibold ${heading}`}>
              {t('approval.complianceCoachTitle', {
                defaultValue: 'Compliance coach',
              })}
            </h3>
            <p className={`text-sm ${body} mt-0.5`}>
              {t('approval.complianceCoachSummary', {
                low: summary.low_score_count,
                unscored: summary.unscored_count,
                threshold: summary.threshold,
                defaultValue: `${summary.low_score_count} entries below ${summary.threshold}% and ${summary.unscored_count} not yet analyzed. Improve them before approval to strengthen the claim.`,
              })}
              {typeof summary.average_score === 'number' && (
                <>
                  {' '}
                  {t('approval.complianceAverage', {
                    avg: summary.average_score,
                    defaultValue: `Average score: ${summary.average_score}%.`,
                  })}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-3">
          {summary.unscored_count > 0 && (
            <button
              onClick={handleAnalyzePending}
              disabled={queuing}
              className={`text-sm font-medium underline disabled:opacity-50 ${heading}`}
            >
              {queuing
                ? t('approval.analyzing', { defaultValue: 'Queuing…' })
                : t('approval.analyzeUnscored', {
                    count: summary.unscored_count,
                    defaultValue: `Analyze ${summary.unscored_count} unscored`,
                  })}
            </button>
          )}
          {summary.low_score_entries.length > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className={`text-sm font-medium underline ${heading}`}
            >
              {expanded
                ? t('common.hide', { defaultValue: 'Hide' })
                : t('approval.reviewLowScores', { defaultValue: 'Review' })}
            </button>
          )}
        </div>
      </div>

      {expanded && summary.low_score_entries.length > 0 && (
        <ul className="mt-4 space-y-3">
          {summary.low_score_entries.map((entry) => {
            const suggestions = (entry.ai_suggestions || [])
              .map((s) => s?.message)
              .filter((m): m is string => Boolean(m));
            return (
              <li
                key={entry.id}
                className={`rounded-lg border p-3 ${
                  isDark
                    ? 'border-slate-700 bg-slate-800/60'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span
                    className={`text-sm font-medium ${
                      isDark ? 'text-slate-200' : 'text-slate-800'
                    }`}
                  >
                    {entry.user?.full_name || '—'}
                    {entry.project?.name ? ` · ${entry.project.name}` : ''}
                  </span>
                  <span
                    className={`text-sm font-bold ${complianceScoreColor(
                      entry.ai_confidence_score,
                      isDark,
                    )}`}
                  >
                    {typeof entry.ai_confidence_score === 'number'
                      ? `${Math.round(entry.ai_confidence_score)}%`
                      : t('approval.notAnalyzed', { defaultValue: 'Not analyzed' })}
                  </span>
                </div>
                <p
                  className={`text-sm mt-1 ${
                    isDark ? 'text-slate-400' : 'text-slate-600'
                  }`}
                >
                  {entry.description}
                </p>
                {suggestions.length > 0 && (
                  <ul
                    className={`mt-2 list-disc pl-5 space-y-1 text-sm ${
                      isDark ? 'text-slate-300' : 'text-slate-700'
                    }`}
                  >
                    {suggestions.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ComplianceCoach;
